"use client";
import { formatDate } from "@/lib/time";
import PageTitle from "../layout/PageTitle";
import { Tabs } from "../ui/Tabs/Tabs";
import { startTransition, useEffect, useRef, useState } from "react";
import PageContent from "../layout/PageContent";
import { CardCollectionTable } from "@/features/card-collection/components/CardCollectionTable";
import { useCardCollection } from "@/features/card-collection/hooks/useCardCollection";
import { useCardCollectionCardChangedEvent } from "@/features/card-collection/hooks/useCardCollectionCardChangedEvent";

const TabContent = (props: {
  isSelected: boolean;
  count: number;
  label: string;
}) => {
  return (
    <div className="flex items-center gap-1.5">
      {props.label}
      <div className="flex items-center gap-1.5">
        <div
          className={
            props.isSelected ? "h-3 w-px bg-[#dcdce0]" : "h-3 w-px bg-[#d0d0d6]"
          }
        />
        <span
          className={
            props.isSelected
              ? "text-[12.5px] font-semibold text-[#8a94a6]"
              : "text-[12.5px] font-semibold text-[#b0b6c0]"
          }
        >
          {props.count}
        </span>
      </div>
    </div>
  );
};

type Tab = "all" | "noccof";

const CardCollection = () => {
  const [selectedTab, setSelectedTab] = useState<Tab>("all");
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedTab]);

  const hasCardFilter = selectedTab === "noccof" ? false : undefined;

  const cardCollection = useCardCollection({
    hasCard: hasCardFilter,
  });

  useCardCollectionCardChangedEvent();

  return (
    <>
      <PageTitle
        title="Card Collection"
        subtitle={formatDate(new Date(), "eeee, MMM d")}
        bottomContent={
          <div className="flex items-center justify-between gap-4">
            <Tabs
              tabs={[
                {
                  id: "all",
                  label: (isSelected) => (
                    <TabContent
                      isSelected={isSelected}
                      count={cardCollection.extra?.summary?.totalCount ?? 0}
                      label="All"
                    />
                  ),
                },
                {
                  id: "noccof",
                  label: (isSelected) => (
                    <TabContent
                      isSelected={isSelected}
                      count={cardCollection.extra?.summary?.noCardCount ?? 0}
                      label="No Card on File"
                    />
                  ),
                },
              ]}
              selected={selectedTab}
              onChange={(id) => startTransition(() => setSelectedTab(id))}
            />
          </div>
        }
      />
      <PageContent>
        <CardCollectionTable
          ref={contentRef}
          {...cardCollection}
          isFetching={cardCollection.isRefetchingWithNewParams}
        />
      </PageContent>
    </>
  );
};

export default CardCollection;
